import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Award, Users, Star, Phone } from 'lucide-react';
import { COMPANY_INFO, fetchCollection } from '../data';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadAboutPage() {
      try {
        setLoading(true);
        const pages = await fetchCollection('pages');
        const matched = pages.find((p) => p.slug?.da === 'om-os' || p.title === 'Om os');
        setPageData(matched);
      } catch (error) {
        console.error('Error loading About page:', error);
      } finally {
        setLoading(false);
      }
    }
    loadAboutPage();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-brand-dark/50 pt-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-blue border-t-transparent mb-4" />
        <p className="text-base">Indlæser...</p>
      </div>
    );
  }

  // Split content after first paragraph to place image divider
  const splitParagraph = '</p>';
  const firstParagraphIndex = pageData?.content ? pageData.content.indexOf(splitParagraph) : -1;
  const introContent = firstParagraphIndex !== -1 
    ? pageData.content.slice(0, firstParagraphIndex + splitParagraph.length) 
    : pageData?.content || '';
  const restContent = firstParagraphIndex !== -1 
    ? pageData.content.slice(firstParagraphIndex + splitParagraph.length) 
    : '';

  return (
    <div className="pt-20 space-y-12 sm:space-y-16">
      {/* Hero Section with Background Image */}
      <section className="relative pt-12 pb-10 sm:pt-16 sm:pb-14 overflow-hidden bg-brand-deep text-white">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg"
            alt="To smilende glarmestre fra Energirude Eksperten foran firmabilen"
            className="w-full h-full object-cover object-[center_35%]"
          />
          {/* Nedtonet mørk/neutral gradient overlay for mindre kraftig blå baggrund og god tekstkontrast */}
          <div className="absolute inset-0 bg-slate-900/60 sm:bg-slate-900/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-900/70 to-slate-900/40" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 text-left space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold tracking-wider text-brand-light uppercase bg-white/15 border border-white/25 px-3.5 py-1.5 rounded-full inline-block backdrop-blur-xs shadow-2xs">
                Hvem er vi
              </span>
              <span className="text-xs font-semibold text-white/80 bg-brand-blue/30 border border-brand-blue/40 px-3 py-1 rounded-full">
                Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
              {pageData?.meta_title || 'Glarmester med Høj Fleksibilitet og Stærke Priser'}
            </h1>
            {(() => {
              const rawDesc = pageData?.meta_description || 'Energirude Eksperten leverer Kvalitets Arbejde til Konkurrencedygtige Priser. Kontakt os for et Prisstærkt Tilbud ☏ Ring på tlf: 21 82 80 00';
              // Check if phone string exists in text
              const phoneMatch = rawDesc.match(/(.*?)(?:☏|\s*Ring\s*på\s*tlf:?)\s*(?:Ring\s*på\s*tlf:?\s*)?(\d{2}\s*\d{2}\s*\d{2}\s*\d{2}|\+?[\d\s]+)$/i);
              
              if (phoneMatch) {
                const textBefore = phoneMatch[1].trim();
                const phoneNum = phoneMatch[2].trim();
                return (
                  <div className="space-y-5">
                    <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
                      {textBefore.startsWith('Energirude Eksperten') ? (
                        <>
                          <Link to="/" className="text-white hover:text-brand-light underline font-medium">Energirude Eksperten</Link>
                          {textBefore.replace(/^Energirude Eksperten/, '')}
                        </>
                      ) : (
                        textBefore
                      )}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                      <a
                        href={`tel:${phoneNum.replace(/\s+/g, '')}`}
                        className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-brand-blue text-white font-bold text-sm sm:text-base hover:bg-white hover:text-brand-deep transition-all shadow-md hover:shadow-lg"
                      >
                        <Phone className="w-4 h-4" />
                        <span>Ring på tlf: {phoneNum}</span>
                      </a>
                    </div>
                  </div>
                );
              }

              return (
                <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
                  {rawDesc.startsWith('Energirude Eksperten') ? (
                    <>
                      <Link to="/" className="text-white hover:text-brand-light underline font-medium">Energirude Eksperten</Link>
                      {rawDesc.replace(/^Energirude Eksperten/, '')}
                    </>
                  ) : (
                    rawDesc
                  )}
                </p>
              );
            })()}
          </div>

          <div className="lg:col-span-5 w-full max-w-md mx-auto lg:max-w-none">
            <div className="bg-white border border-brand-light/80 p-6 sm:p-7 rounded-2xl space-y-4 shadow-sm text-center overflow-hidden transition-all hover:shadow-md">
              <div className="-mx-6 -mt-6 sm:-mx-7 sm:-mt-7 mb-2 bg-brand-light/40 border-b border-brand-light/60">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-anbefalinger-haandvaerkerdk.png" 
                  alt="Kunderne anbefaler Energirude Eksperten på håndværker.dk med mere end 50 anmeldelser" 
                  className="w-full h-auto object-cover block"
                />
              </div>
              <div className="flex items-center justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-brand-green text-brand-green" />
                ))}
              </div>
              <h3 className="text-lg font-bold text-brand-deep">Fremragende på Trustpilot</h3>
              <p className="text-xs sm:text-sm text-brand-dark/70">
                Vi har en score på 4,6 ud af 5 stjerner baseret på 21 anmeldelser, samt 4,9 ud af 5 på håndværker.dk.
              </p>
              <a
                href={COMPANY_INFO.trustpilotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-brand-light/60 hover:bg-brand-light text-brand-blue hover:text-brand-deep font-bold text-sm transition-colors"
              >
                Læs anmeldelser <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-10">
          {pageData?.content ? (
            <>
              {/* Intro Content (Leading paragraph) */}
              <div
                className="prose prose-brand max-w-4xl text-brand-dark/90 leading-relaxed
                  prose-headings:text-brand-deep prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:sm:text-3xl prose-h2:mb-4
                  prose-p:text-base sm:prose-p:text-lg prose-p:text-brand-dark/80 prose-p:leading-relaxed prose-p:mb-0
                  prose-strong:text-brand-deep prose-strong:font-bold"
                dangerouslySetInnerHTML={{ __html: introContent }}
              />

              {/* Image & Key Stats splitting the text */}
              <div className="py-3">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
                  <div className="md:col-span-8 rounded-2xl overflow-hidden border border-brand-light/80 shadow-md aspect-16/10 sm:aspect-16/9 bg-brand-light">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-servicebil-glasstativ.jpg"
                      alt="Energirude Ekspertens firmabil med professionelt glasstativ parkeret ved villa"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
                    <div className="bg-white border border-brand-light p-6 sm:p-7 rounded-2xl text-center flex flex-col justify-center items-center shadow-xs hover:border-brand-blue/30 transition-colors">
                      <span className="block text-3xl sm:text-4xl font-extrabold text-brand-blue">25+</span>
                      <span className="block text-xs text-brand-dark/70 uppercase font-semibold mt-1.5 tracking-wider">Års Erfaring</span>
                    </div>
                    <div className="bg-white border border-brand-light p-6 sm:p-7 rounded-2xl text-center flex flex-col justify-center items-center shadow-xs hover:border-brand-green/30 transition-colors">
                      <span className="block text-3xl sm:text-4xl font-extrabold text-brand-green">100%</span>
                      <span className="block text-xs text-brand-dark/70 uppercase font-semibold mt-1.5 tracking-wider">Tilfredshed</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remaining Rich Text Content */}
              {restContent && (
                <div
                  className="prose prose-brand max-w-4xl text-brand-dark/90 leading-relaxed
                    prose-headings:text-brand-deep prose-headings:font-bold
                    prose-h2:text-2xl prose-h2:sm:text-3xl prose-h2:mt-10 prose-h2:mb-4
                    prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                    prose-p:text-base prose-p:text-brand-dark/80 prose-p:leading-relaxed prose-p:mb-5
                    prose-strong:text-brand-deep prose-strong:font-bold"
                  dangerouslySetInnerHTML={{ __html: restContent }}
                />
              )}
            </>
          ) : (
            <p className="text-brand-dark/60">Indholdet er under opbygning...</p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-brand-deep text-white rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden border border-brand-blue/20 shadow-xl text-center space-y-6">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,119,199,0.15),transparent_60%)] pointer-events-none" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl mx-auto leading-tight">
            Vil du have et uforpligtende tilbud?
          </h2>
          <p className="text-sm sm:text-base text-brand-light/80 max-w-2xl mx-auto">
            Book et gratis besøg af vores glarmester, der med base i Smørum dækker Egedal, Nordsjælland og store dele af Sjælland med præcis opmåling og rådgivning om den mest optimale løsning til din bolig. Det er 100% gratis og uforpligtende.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/gratis-besoeg"
              className="w-full sm:w-auto bg-brand-blue hover:bg-white hover:text-brand-deep text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 shadow-md"
            >
              Book gratis besøg nu
            </Link>
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="w-full sm:w-auto border border-white/20 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
            >
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// Small helper to render arrow icon
function ArrowRight({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}
